import { isEmpty } from '@intelligence/v3/utils/common';
import { getDateRangeInTitle, getDateRangeFetchParamsByRangeTimeFilterValue } from '@intelligence/v3/utils/date';
import { getDataSourceName, getMetricName } from '@intelligence/v3/components/TableComponents/RatioMetricSortBy/utils';
import { formatGSDTypeFilterValue } from '@intelligence/v3/utils/topcharts';
import { pageParamsFormatMap } from '@intelligence/components/Filter/util';
import { SortMode, SortType } from '@home/enums/common';
import { LANGUAGE_EN, LANGUAGE_CN } from '@home/_config/config';
import * as filterKeyMap from '@intelligence/components/Filter/FilterBase/_config';
import {
  DATASOURCE_KEY_MAP,
  MSCIENCE_METRIC_KEY_MAP,
  GSD_METRIC_KEY_MAP,
  NPD_METRIC_KEY_MAP,
  NEWZOO_METRIC_KEY_MAP,
} from '@intelligence/v3/_config/metrics/keyMap';
import {
  GSD_DATASOURCE_NAME,
  MSCIENCE_DATASOURCE_NAME,
  NPD_DATASOURCE_NAME,
  NEWZOO_DATASOURCE_NAME,
} from '@intelligence/v3/_config/metrics/dataSourceName';
import {
  GSD_TOTAL_REVENUE_METRIC_NAME,
  GSD_DIGITAL_REVENUE_METRIC_NAME,
  GSD_PHYSICAL_REVENUE_METRIC_NAME,
  GSD_TOTAL_UNITS_METRIC_NAME,
  GSD_DIGITAL_UNITS_METRIC_NAME,
  GSD_PHYSICAL_UNITS_METRIC_NAME,
  MSCIENCE_DIGITAL_REVENUE_METRIC_NAME,
  MSCIENCE_DIGITAL_UNITS_METRIC_NAME,
  NPD_PHYSICAL_REVENUE_METRIC_NAME,
  NPD_PHYSICAL_UNITS_METRIC_NAME,
  NEWZOO_TOTAL_REVENUE_METRIC_NAME,
  NEWZOO_TOTAL_PLAYERS_METRIC_NAME,
} from '@intelligence/v3/_config/metrics/metricName';
import {
  GSD_TOTAL_UNITS_METRIC_KEY,
  GSD_DIGITAL_UNITS_METRIC_KEY,
  GSD_PHYSICAL_UNITS_METRIC_KEY,
  GSD_TOTAL_REVENUE_METRIC_KEY,
  GSD_DIGITAL_REVENUE_METRIC_KEY,
  GSD_PHYSICAL_REVENUE_METRIC_KEY,
} from '@intelligence/v3/_config/metrics/gsd';
import {
  MSCIENCE_DIGITAL_REVENUE_METRIC_KEY,
  MSCIENCE_DIGITAL_UNITS_METRIC_KEY,
} from '@intelligence/v3/_config/metrics/mscience';
import { NPD_PHYSICAL_REVENUE_METRIC_KEY, NPD_PHYSICAL_UNITS_METRIC_KEY } from '@intelligence/v3/_config/metrics/npd';
import {
  NEWZOO_TOTAL_REVENUE_METRIC_KEY,
  NEWZOO_TOTAL_PLAYERS_METRIC_KEY,
} from '@intelligence/v3/_config/metrics/newzoo';
import moment from 'moment';

export const DATASOURCE_EXPLANATION_KEY_LIST = [
  [
    'dbi_pcconsole_source_mscience_first',
    'dbi_pcconsole_source_mscience_second',
    'dbi_pcconsole_source_mscience_third',
    'dbi_pcconsole_source_mscience_fourth',
    'dbi_pcconsole_source_mscience_fifth',
  ],
  [
    'dbi_pcconsole_source_npd_first',
    'dbi_pcconsole_source_npd_second',
    'dbi_pcconsole_source_npd_third',
    'dbi_pcconsole_source_npd_fourth',
    'dbi_pcconsole_source_npd_fifth',
    'dbi_pcconsole_source_npd_sixth',
    'dbi_pcconsole_source_npd_seventh',
  ],
  // [
  //   'dbi_pcconsole_source_vginsight_first',
  //   'dbi_pcconsole_source_vginsight_second',
  //   'dbi_pcconsole_source_vginsight_third',
  //   'dbi_pcconsole_source_vginsight_fourth',
  //   'dbi_pcconsole_source_vginsight_fifth',
  // ],
  [
    'dbi_pcconsole_source_gsd_first',
    'dbi_pcconsole_source_gsd_second',
    'dbi_pcconsole_source_gsd_third',
    'dbi_pcconsole_source_gsd_fourth',
    'dbi_pcconsole_source_gsd_fifth',
    'dbi_pcconsole_source_gsd_sixth',
    'dbi_pcconsole_source_gsd_seventh',
  ],
];

export const DATASOURCE_EXPLANATION_DATASOURCE_KEY_LIST = ['MScience', 'NPD', 'GSD'];

export const YEARLY_METRIC_KEYS = [NEWZOO_TOTAL_REVENUE_METRIC_KEY, NEWZOO_TOTAL_PLAYERS_METRIC_KEY];

export const YEARLY_METRICS: ICommonMetric[] = [
  {
    key: NEWZOO_TOTAL_REVENUE_METRIC_KEY,
    dataSource: DATASOURCE_KEY_MAP.NEWZOO,
    metric: NEWZOO_METRIC_KEY_MAP.TOTAL_REVENUE,
    dataSourceName: NEWZOO_DATASOURCE_NAME,
    metricName: NEWZOO_TOTAL_REVENUE_METRIC_NAME,
  },
  {
    key: NEWZOO_TOTAL_PLAYERS_METRIC_KEY,
    dataSource: DATASOURCE_KEY_MAP.NEWZOO,
    metric: NEWZOO_METRIC_KEY_MAP.TOTAL_PLAYERS,
    dataSourceName: NEWZOO_DATASOURCE_NAME,
    metricName: NEWZOO_TOTAL_PLAYERS_METRIC_NAME,
  },
];

/**
 * 日期粒度指标列表
 */
export const METRICS_GROUP_BY_GRANULARITY: IMetricsGroupByGranularity[] = [
  {
    granularity: 'weekly',
    metrics: [
      {
        key: GSD_TOTAL_UNITS_METRIC_KEY,
        dataSource: DATASOURCE_KEY_MAP.GSD,
        metric: GSD_METRIC_KEY_MAP.TOTAL_UNITS,
        dataSourceName: GSD_DATASOURCE_NAME,
        metricName: GSD_TOTAL_UNITS_METRIC_NAME,
      },
      {
        key: GSD_DIGITAL_UNITS_METRIC_KEY,
        dataSource: DATASOURCE_KEY_MAP.GSD,
        metric: GSD_METRIC_KEY_MAP.DIGITAL_UNITS,
        dataSourceName: GSD_DATASOURCE_NAME,
        metricName: GSD_DIGITAL_UNITS_METRIC_NAME,
      },
      {
        key: GSD_PHYSICAL_UNITS_METRIC_KEY,
        dataSource: DATASOURCE_KEY_MAP.GSD,
        metric: GSD_METRIC_KEY_MAP.PHYSICAL_UNITS,
        dataSourceName: GSD_DATASOURCE_NAME,
        metricName: GSD_PHYSICAL_UNITS_METRIC_NAME,
      },
      {
        key: GSD_TOTAL_REVENUE_METRIC_KEY,
        dataSource: DATASOURCE_KEY_MAP.GSD,
        metric: GSD_METRIC_KEY_MAP.TOTAL_REVENUE,
        dataSourceName: GSD_DATASOURCE_NAME,
        metricName: GSD_TOTAL_REVENUE_METRIC_NAME,
      },
      {
        key: GSD_DIGITAL_REVENUE_METRIC_KEY,
        dataSource: DATASOURCE_KEY_MAP.GSD,
        metric: GSD_METRIC_KEY_MAP.DIGITAL_REVENUE,
        dataSourceName: GSD_DATASOURCE_NAME,
        metricName: GSD_DIGITAL_REVENUE_METRIC_NAME,
      },
      {
        key: GSD_PHYSICAL_REVENUE_METRIC_KEY,
        dataSource: DATASOURCE_KEY_MAP.GSD,
        metric: GSD_METRIC_KEY_MAP.PHYSICAL_REVENUE,
        dataSourceName: GSD_DATASOURCE_NAME,
        metricName: GSD_PHYSICAL_REVENUE_METRIC_NAME,
      },
    ],
  },
  {
    granularity: 'monthly',
    metrics: [
      {
        key: MSCIENCE_DIGITAL_REVENUE_METRIC_KEY,
        dataSource: DATASOURCE_KEY_MAP.MSCIENCE,
        metric: MSCIENCE_METRIC_KEY_MAP.DIGITAL_REVENUE,
        dataSourceName: MSCIENCE_DATASOURCE_NAME,
        metricName: MSCIENCE_DIGITAL_REVENUE_METRIC_NAME,
      },
      {
        key: MSCIENCE_DIGITAL_UNITS_METRIC_KEY,
        dataSource: DATASOURCE_KEY_MAP.MSCIENCE,
        metric: MSCIENCE_METRIC_KEY_MAP.DIGITAL_UNITS,
        dataSourceName: MSCIENCE_DATASOURCE_NAME,
        metricName: MSCIENCE_DIGITAL_UNITS_METRIC_NAME,
      },
      {
        key: NPD_PHYSICAL_REVENUE_METRIC_KEY,
        dataSource: DATASOURCE_KEY_MAP.NPD,
        metric: NPD_METRIC_KEY_MAP.PHYSICAL_REVENUE,
        dataSourceName: NPD_DATASOURCE_NAME,
        metricName: NPD_PHYSICAL_REVENUE_METRIC_NAME,
      },
      {
        key: NPD_PHYSICAL_UNITS_METRIC_KEY,
        dataSource: DATASOURCE_KEY_MAP.NPD,
        metric: NPD_METRIC_KEY_MAP.PHYSICAL_UNITS,
        dataSourceName: NPD_DATASOURCE_NAME,
        metricName: NPD_PHYSICAL_UNITS_METRIC_NAME,
      },
    ],
  },
  {
    granularity: 'yearly',
    metrics: YEARLY_METRICS,
  },
  {
    granularity: 'monthlypcandconsole',
    metrics: [
      {
        key: MSCIENCE_DIGITAL_REVENUE_METRIC_KEY,
        dataSource: DATASOURCE_KEY_MAP.MSCIENCE,
        metric: MSCIENCE_METRIC_KEY_MAP.DIGITAL_REVENUE,
        dataSourceName: MSCIENCE_DATASOURCE_NAME,
        metricName: MSCIENCE_DIGITAL_REVENUE_METRIC_NAME,
      },
      {
        key: NPD_PHYSICAL_REVENUE_METRIC_KEY,
        dataSource: DATASOURCE_KEY_MAP.NPD,
        metric: NPD_METRIC_KEY_MAP.PHYSICAL_REVENUE,
        dataSourceName: NPD_DATASOURCE_NAME,
        metricName: NPD_PHYSICAL_REVENUE_METRIC_NAME,
      },
    ],
  },
];

/**
 * 获取Market Profile页面PCConsoleGames 指定日期粒度对应的指标列表
 * @param granularity 日期粒度
 * @return 日期粒度对应的指标列表
 */
export const getGranularityMetrics = (granularity: IGranularity): ICommonMetric[] => {
  const granularityMetricsData = METRICS_GROUP_BY_GRANULARITY.find((item) => granularity === item?.granularity);
  const granularityMetrics = granularityMetricsData?.metrics;
  if (!Array.isArray(granularityMetrics) || granularityMetrics.length === 0) return [];
  return granularityMetrics.map((item) => {
    const itemDataSource = item?.dataSource || '';
    const itemMetric = item?.metric || '';
    return {
      ...item,
      dataSourceName: {
        [LANGUAGE_EN]: getDataSourceName(LANGUAGE_EN, itemDataSource),
        [LANGUAGE_CN]: getDataSourceName(LANGUAGE_CN, itemDataSource),
      },
      metricName: {
        [LANGUAGE_EN]: getMetricName(LANGUAGE_EN, itemDataSource, itemMetric),
        [LANGUAGE_CN]: getMetricName(LANGUAGE_CN, itemDataSource, itemMetric),
      },
    };
  });
};

/**
 * 获取Market Profile页面PCConsoleGames 指定日期粒度对应的默认指标（echarts可视化勾选）
 * @param granularity 日期粒度
 * @return 日期粒度对应的指标
 */
export const getGranularityMetric = (granularity: IGranularity): ICommonMetric => {
  const metrics = getGranularityMetrics(granularity);
  return { ...metrics?.[0], granularity };
};

/**
 * 获取Market Profile页面PCConsoleGames 指定日期粒度对应的默认指标（指标宽表默认排序）
 * @param granularity 日期粒度
 * @return 日期粒度对应的排序状态列表
 */
export const getGranularitySorts = (granularity: IGranularity): IRatioMetricSortState<IGenreMarketProfile>[] => {
  const metric = getGranularityMetric(granularity);
  return [
    {
      dataSource: metric?.dataSource,
      metric: metric?.metric,
      type: SortType.Desc,
      mode: SortMode.Number,
    },
  ];
};

/**
 * 是否为newzoo的年粒度指标
 * @param metric 指标
 */
export const getIsNewzooYearlyMetric = (metric: IPrimaryKeyMetric) => {
  return (
    DATASOURCE_KEY_MAP.NEWZOO === metric?.dataSource &&
    [NEWZOO_METRIC_KEY_MAP.TOTAL_REVENUE, NEWZOO_METRIC_KEY_MAP.TOTAL_PLAYERS].includes(metric?.metric)
  );
};

/**
 * 是否为需要重新查询有数据日期范围的指标
 * @param metric 指标
 */
export const getIsReCheckDateRangeMetric = (metric: IPrimaryKeyMetric) => {
  const dataSourceKey = metric?.dataSource;
  const metricKey = metric?.metric;
  if (DATASOURCE_KEY_MAP.GSD === dataSourceKey && [GSD_METRIC_KEY_MAP.DIGITAL_REVENUE].includes(metricKey)) return true;
  return false;
};

/**
 * 判断是否没有指定指标数据
 * @param list 接口返回列表数据
 */
export const getIsMetricNoData = (metric: IPrimaryKeyMetric, list?: IGenreMarketProfile[] | null) => {
  if (!Array.isArray(list) || list.length === 0) return false;
  return list.every((item) => {
    return isEmpty(item?.[metric?.key]);
  });
};

/**
 * 特殊处理newzoo指标名称
 * Total Revenue、Total Players指标需要在指标名中加上年份信息
 * @param locale 当前语言环境
 * @param params 其他参数
 * @param params.metric 指标
 * @param params.metricName 指标名称（通用指标名称获取逻辑生成的指标名）
 * @param params.startDate 开始日期
 * @param params.endDate 结束日期
 * @return 指标名
 */
export const getMetricNameSpecial = (
  locale: string,
  params: {
    metric: IMetric;
    metricName: string;
    startDate: string;
    endDate: string;
  },
) => {
  const metric = params?.metric;
  const metricName = params?.metricName;
  const dataSourceKey = metric?.dataSource || '';
  const metricKey = metric?.metric || '';
  if (dataSourceKey === DATASOURCE_KEY_MAP.NEWZOO) {
    const startDate = params?.startDate || '';
    const endDate = params?.endDate || '';
    const yearStr = getDateRangeInTitle('yearly', startDate, endDate);
    if (NEWZOO_METRIC_KEY_MAP.TOTAL_REVENUE === metricKey) {
      return locale === LANGUAGE_CN ? `总收入(${yearStr})($)` : `Total Revenue(${yearStr})($)`;
    }
    if (NEWZOO_METRIC_KEY_MAP.TOTAL_PLAYERS === metricKey) {
      return locale === LANGUAGE_CN ? `总玩家数量(${yearStr})` : `Total Players(${yearStr})`;
    }
  }
  return metricName;
};

/**
 * 特殊处理newzoo指标名称--PcAndConsole
 * Total Revenue、Total Players指标需要在指标名中加上年份信息
 * @param locale 当前语言环境
 * @param params 其他参数
 * @param params.metric 指标
 * @param params.metricName 指标名称（通用指标名称获取逻辑生成的指标名）
 * @param params.startDate 开始日期
 * @param params.endDate 结束日期
 * @return 指标名
 */
export const getMetricNameSpecialPcAndConsole = (
  locale: string,
  params: {
    metric: IMetric;
    metricName: string;
    startDate: string;
    endDate: string;
  },
) => {
  const metric = params?.metric;
  const metricName = params?.metricName;
  const metricKey = metric?.metric || '';
  const startDate = params?.startDate || '';
  const endDate = params?.endDate || '';

  const startYear = Number(moment(startDate).format('YYYY-MM-DD').slice(0, 4));
  const endYear = Number(moment(endDate).format('YYYY-MM-DD').slice(0, 4));

  if (NEWZOO_METRIC_KEY_MAP.TOTAL_REVENUE === metricKey) {
    if (startYear === endYear) {
      const yearStr = `in ${startYear}`;
      return locale === LANGUAGE_CN ? `总收入(${yearStr})($)` : `Total Revenue(${yearStr})($)`;
    } else {
      const yearStr = `Sum of ${startYear}-${endYear}`;
      return locale === LANGUAGE_CN ? `总收入(${yearStr})($)` : `Total Revenue(${yearStr})($)`;
    }
  }
  if (NEWZOO_METRIC_KEY_MAP.TOTAL_PLAYERS === metricKey) {
    if (startYear === endYear) {
      const yearStr = `in ${startYear}`;
      return locale === LANGUAGE_CN ? `总玩家数量(${yearStr})` : `Total Players(${yearStr})`;
    } else {
      const yearStr = `Sum of ${startYear}-${endYear}`;
      return locale === LANGUAGE_CN ? `总玩家数量(${yearStr})` : `Total Players(${yearStr})`;
    }
  }

  return metricName;
};

/**
 * 把Market Profile页面PCConsole榜单页筛选器value转换为接口需要的参数（筛选器的参数）
 * @param filterValue 页面筛选器的value
 * @return 页面筛选器的接口入参
 */
export const formatFilterValue = (filterValue?: IRecord): IMarketProfileFilterFetchParams => {
  const genre = filterValue?.[filterKeyMap.GENRETOR];
  const rangeTime = filterValue?.[filterKeyMap.RANGE_TIME];
  const dateRangeFetchParams = getDateRangeFetchParamsByRangeTimeFilterValue(rangeTime);
  const region = filterValue?.[filterKeyMap.REGION];
  const publisher = filterValue?.[filterKeyMap.PUBLISHER];

  return {
    ...dateRangeFetchParams,
    markets: region?.realMarkets || [],
    filter: {
      iegg_genre_req: pageParamsFormatMap[filterKeyMap.GENRETOR]?.(genre)?.iegg_genre_req || [],
      gsd_types: formatGSDTypeFilterValue(publisher?.value),
    },
  };
};

/**
 * 追加固定展示的指标列表
 * @param metrics 日期粒度对应的指标列表
 * @return 要展示的指标列表
 */
export const appendFixedMetrics = (metrics?: ICommonMetric[] | null): ICommonMetric[] => {
  if (!Array.isArray(metrics) || metrics.length === 0) return [...YEARLY_METRICS];
  return [...metrics, ...YEARLY_METRICS];
};
